import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Zap } from "lucide-react";
import axios from "axios";
import { useLang } from "@/lib/lang";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import AdBanner from "@/components/AdBanner";
import CartDrawer from "@/components/CartDrawer";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Store() {
  const { lang } = useLang();
  const cart = useCart();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [currency, setCurrency] = useState("CAD");
  const [selectedSize, setSelectedSize] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/store/products`).then(r => {
      setProducts(r.data.products);
      setCurrency(r.data.currency);
    }).finally(() => setLoading(false));
  }, []);

  const T = lang === "fr" ? FR : EN;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="store-page">
      <NavBar />

      {/* Floating cart button */}
      <button data-testid="cart-toggle-btn" onClick={() => cart.setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 h-12 rounded-full bg-[#8b6f3d] hover:bg-[#a1854b] text-white shadow-xl transition-colors">
        <ShoppingBag size={16} />
        <span className="text-sm font-medium">{T.cart}</span>
        {cart.count > 0 && (
          <span data-testid="cart-count-badge" className="min-w-[22px] h-[22px] rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center px-1">
            {cart.count}
          </span>
        )}
      </button>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-12 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">{T.tag}</span>
        </div>
        <h1 className="font-serif-mystery text-4xl md:text-5xl lg:text-6xl text-zinc-900 leading-[1.05] mb-4" data-testid="store-title">
          {T.title}<br /><span className="italic text-[#8b6f3d]">{T.titleItalic}</span>
        </h1>
        <p className="text-lg text-zinc-600 max-w-2xl">{T.intro}</p>
      </section>

      {/* Moon Explorer feature banner */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 mb-8">
        <a href="/moon-explorer" data-testid="moon-explorer-banner"
          className="block rounded-2xl overflow-hidden relative p-6 md:p-10 text-white transition-transform hover:-translate-y-1 shadow-xl"
          style={{ background: "radial-gradient(circle at 30% 30%, rgba(168,85,247,0.6), transparent 60%), radial-gradient(circle at 70% 70%, rgba(59,130,246,0.5), transparent 55%), #0a0a1e" }}>
          <div className="text-xs font-mono-data uppercase tracking-[0.3em] text-purple-200 mb-2">CosmoReach · {lang === "fr" ? "Nouveau projet" : "New project"}</div>
          <h2 className="font-serif-mystery text-3xl md:text-5xl mb-3">🌙 Moon Explorer</h2>
          <p className="text-white/80 max-w-2xl mb-4">{lang === "fr" ? "L'espace enfin accessible à tous. Découvrez, pilotez et explorez la Lune depuis votre écran. Réservez votre place à partir de 20 $." : "Space finally accessible to everyone. Discover, pilot and explore the Moon from your screen. Reserve your seat from $20."}</p>
          <span className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white text-slate-900 text-sm font-medium">
            🎟️ {lang === "fr" ? "Voir le projet" : "View project"}
          </span>
        </a>
      </section>

      {/* Products grid */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        {loading ? (
          <p className="text-center text-zinc-500 py-10">{T.loading}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
            {products.map(p => {
              const size = selectedSize[p.id] || p.sizes[1] || p.sizes[0];
              return (
                <article key={p.id} className="group border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all bg-white"
                  data-testid={`product-card-${p.id}`}>
                  <div className="aspect-square bg-zinc-50 overflow-hidden">
                    <img src={p.image} alt={p.name[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-mystery text-xl text-zinc-900 leading-tight mb-2">{p.name[lang]}</h3>
                    <p className="text-sm text-zinc-600 leading-relaxed mb-4 line-clamp-3">{p.description[lang]}</p>

                    <div className="mb-4">
                      <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{T.size}</div>
                      <div className="flex flex-wrap gap-1.5" data-testid={`sizes-${p.id}`}>
                        {p.sizes.map(s => (
                          <button key={s} data-testid={`size-${p.id}-${s}`}
                            onClick={() => setSelectedSize(prev => ({ ...prev, [p.id]: s }))}
                            className={`px-3 py-1 rounded-full text-xs font-mono-data border transition-colors ${
                              size === s ? "bg-[#8b6f3d] text-white border-[#8b6f3d]" : "border-zinc-300 text-zinc-700 hover:border-[#8b6f3d]"
                            }`}>{s}</button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <div className="font-serif-mystery text-2xl text-[#8b6f3d]" data-testid={`price-${p.id}`}>
                        {p.price.toFixed(2)} {currency}
                      </div>
                      <Button
                        data-testid={`add-to-cart-${p.id}`}
                        onClick={() => cart.addItem(p, size)}
                        className="bg-zinc-900 hover:bg-[#8b6f3d] text-white rounded-full h-10 px-4 transition-colors">
                        <ShoppingBag size={14} className="mr-1.5" />
                        {T.add}
                      </Button>
                    </div>
                    <Button
                      data-testid={`buy-now-${p.id}`}
                      onClick={() => { cart.addItem(p, size); setTimeout(() => nav("/checkout"), 100); }}
                      className="w-full bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-11 transition-colors">
                      <Zap size={14} className="mr-1.5" />
                      {T.buyNow}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12">
        <AdBanner testId="store-mid-ad" />
      </section>

      {/* Trust box */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: T.trust1Title, body: T.trust1Body },
            { title: T.trust2Title, body: T.trust2Body },
            { title: T.trust3Title, body: T.trust3Body },
          ].map((t, i) => (
            <div key={i} className="border border-zinc-200 rounded-2xl p-6" data-testid={`trust-${i}`}>
              <h4 className="font-serif-mystery text-xl text-[#8b6f3d] mb-2">{t.title}</h4>
              <p className="text-sm text-zinc-600 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12">
        <AdBanner testId="store-bottom-ad" />
      </section>

      <Footer />
      <CartDrawer />
    </div>
  );
}

const FR = {
  cart: "Panier",
  tag: "Boutique officielle",
  title: "Portez le mystère",
  titleItalic: "sur vous",
  intro: "T-shirts exclusifs WorldMysteriesMap. Édition limitée, imprimés uniques inspirés des lieux légendaires de la carte. Livraison mondiale.",
  size: "Taille",
  add: "Ajouter",
  buyNow: "Acheter maintenant",
  loading: "Chargement des produits…",
  trust1Title: "Paiement 100% PayPal",
  trust1Body: "Paiement sécurisé via PayPal. Aucune information bancaire stockée sur nos serveurs.",
  trust2Title: "Éditions limitées",
  trust2Body: "5 designs exclusifs conçus autour de la mythologie mondiale, des OVNIs et des mystères cartographiés.",
  trust3Title: "Livraison mondiale",
  trust3Body: "Expédition internationale forfaitaire. Un email de confirmation vous sera envoyé après paiement.",
};

const EN = {
  cart: "Cart",
  tag: "Official store",
  title: "Wear the mystery",
  titleItalic: "on your sleeve",
  intro: "Exclusive WorldMysteriesMap T-shirts. Limited edition, unique prints inspired by the legendary places on the map. Worldwide shipping.",
  size: "Size",
  add: "Add",
  buyNow: "Buy now",
  loading: "Loading products…",
  trust1Title: "100% PayPal checkout",
  trust1Body: "Secure PayPal payment. No card details stored on our servers.",
  trust2Title: "Limited editions",
  trust2Body: "5 exclusive designs inspired by world mythology, UFOs and mapped mysteries.",
  trust3Title: "Worldwide shipping",
  trust3Body: "Flat-rate international shipping. Confirmation email sent after payment.",
};
